import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "step-6-prepare-for-checkout"
    }}>{`Step 6. Prepare for checkout`}</h1>
    <p>{`Now that all the items have been added to the cart, we can prepare the order for checkout. This process includes the following steps:`}</p>
    <ul>
      <li parentName="ul">{`Estimate shipping costs`}</li>
      <li parentName="ul">{`Set shipping and billing information`}</li>
    </ul>
    <h3 {...{
      "id": "estimate-shipping-costs"
    }}>{`Estimate shipping costs`}</h3>
    <p>{`Adobe Commerce calculates shipping costs for each shipping method that can be applied to the order. In this tutorial, the `}<inlineCode parentName="p">{`flatrate`}</inlineCode>{` ($5 per item) and `}<inlineCode parentName="p">{`tablerate`}</inlineCode>{` shipping methods are active.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Use the `}<inlineCode parentName="p">{`V1/guest-carts/<cartId>/estimate-shipping-methods`}</inlineCode>{` endpoint to estimate shipping costs on behalf of a guest. Do not include an authorization token.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong></p>
    <p><inlineCode parentName="p">{`POST <host>/rest/<store_code>/V1/carts/mine/estimate-shipping-methods`}</inlineCode></p>
    <p><strong parentName="p">{`Headers:`}</strong></p>
    <p><inlineCode parentName="p">{`Content-Type: application/json`}</inlineCode></p>
    <p><inlineCode parentName="p">{`Authorization: Bearer <customer token>`}</inlineCode></p>
    <p><strong parentName="p">{`Payload:`}</strong></p>
    <p>{`The payload contains the shipping address.`}</p>
    <details>
      <summary><b>Show code sample</b></summary>
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`{
  "address": {
    "region": "New York",
    "region_id": 43,
    "region_code": "NY",
    "country_id": "US",
    "street": [
      "123 Oak Ave"
    ],
    "postcode": "10577",
    "city": "Purchase",
    "firstname": "Jane",
    "lastname": "Doe",
    "customer_id": 4,
    "email": "jdoe@example.com",
    "telephone": "(512) 555-1111",
    "same_as_billing": 1
  }
}
`}</code></pre>
    </details>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <p>{`Note that the cost for the `}<inlineCode parentName="p">{`flatrate`}</inlineCode>{` shipping method is $15. The Sprite Yoga Companion Kit bundled product counts as one item. The Advanced Pilates & Yoga item does not have a shipping charge because the customer downloads this item.`}</p>
    <details>
      <summary><b>Show code sample</b></summary>
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`[
  {
    "carrier_code": "flatrate",
    "method_code": "flatrate",
    "carrier_title": "Flat Rate",
    "method_title": "Fixed",
    "amount": 15,
    "base_amount": 15,
    "available": true,
    "error_message": "",
    "price_excl_tax": 15,
    "price_incl_tax": 15
  },
  {
    "carrier_code": "tablerate",
    "method_code": "bestway",
    "carrier_title": "Best Way",
    "method_title": "Table Rate",
    "amount": 5,
    "base_amount": 5,
    "available": true,
    "error_message": "",
    "price_excl_tax": 5,
    "price_incl_tax": 5
  }
]
`}</code></pre>
    </details>
    <h3 {...{
      "id": "set-shipping-and-billing-information"
    }}>{`Set shipping and billing information`}</h3>
    <p>{`In this call, you specify the shipping and billing addresses, as well as the selected `}<inlineCode parentName="p">{`carrier_code`}</inlineCode>{` and `}<inlineCode parentName="p">{`method_code`}</inlineCode>{`. Since the Table Rate shipping method costs only $5, the customer selected this option.`}</p>
    <p>{`Commerce returnsa list of payment options and calculates the order totals.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Use the `}<inlineCode parentName="p">{`V1/guest-carts/<cartId>/shipping-information`}</inlineCode>{` endpoint to set the billing and shipping information on behalf of a guest. Do not include an authorization token.`}</p>
    <p><strong parentName="p">{`Endpoint:`}</strong></p>
    <p><inlineCode parentName="p">{`POST <host>/rest/<store_code>/V1/carts/mine/shipping-information`}</inlineCode></p>
    <p><strong parentName="p">{`Headers:`}</strong></p>
    <p><inlineCode parentName="p">{`Content-Type: application/json`}</inlineCode></p>
    <p><inlineCode parentName="p">{`Authorization: Bearer <customer token>`}</inlineCode></p>
    <p><strong parentName="p">{`Payload:`}</strong></p>
    <details>
      <summary><b>Show code sample</b></summary>
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`{
  "addressInformation": {
    "shipping_address": {
      "region": "New York",
      "region_id": 43,
      "region_code": "NY",
      "country_id": "US",
      "street": [
        "123 Oak Ave"
      ],
      "postcode": "10577",
      "city": "Purchase",
      "firstname": "Jane",
      "lastname": "Doe",
      "email": "jdoe@example.com",
      "telephone": "512-555-1111"
    },
    "billing_address": {
      "region": "New York",
      "region_id": 43,
      "region_code": "NY",
      "country_id": "US",
      "street": [
        "123 Oak Ave"
      ],
      "postcode": "10577",
      "city": "Purchase",
      "firstname": "Jane",
      "lastname": "Doe",
      "email": "jdoe@example.com",
      "telephone": "512-555-1111"
    },
    "shipping_carrier_code": "tablerate",
    "shipping_method_code": "bestway"
  }
}
`}</code></pre>
    </details>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <p>{`The subtotal of the order is $160, and shipping charges are $5. The grand total is $165.`}</p>
    <p>{`The available payment methods are `}<inlineCode parentName="p">{`banktransfer`}</inlineCode>{` and `}<inlineCode parentName="p">{`checkmo`}</inlineCode>{`. The customer will specify a payment method in the next step.`}</p>
    <details>
      <summary><b>Show code sample</b></summary>
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`{
  "payment_methods": [
    {
      "code": "cashondelivery",
      "title": "Cash On Delivery"
    },
    {
      "code": "banktransfer",
      "title": "Bank Transfer Payment"
    },
    {
      "code": "purchaseorder",
      "title": "Purchase Order"
    },
    {
      "code": "checkmo",
      "title": "Check / Money order"
    }
  ],
  "totals": {
    "grand_total": 165,
    "base_grand_total": 165,
    "subtotal": 160,
    "base_subtotal": 160,
    "discount_amount": 0,
    "base_discount_amount": 0,
    "subtotal_with_discount": 160,
    "base_subtotal_with_discount": 160,
    "shipping_amount": 5,
    "base_shipping_amount": 5,
    "shipping_discount_amount": 0,
    "base_shipping_discount_amount": 0,
    "tax_amount": 0,
    "base_tax_amount": 0,
    "weee_tax_applied_amount": null,
    "shipping_tax_amount": 0,
    "base_shipping_tax_amount": 0,
    "subtotal_incl_tax": 160,
    "shipping_incl_tax": 5,
    "base_shipping_incl_tax": 5,
    "base_currency_code": "USD",
    "quote_currency_code": "USD",
    "items_qty": 4,
    "items": [
      {
        "item_id": 6,
        "price": 22,
        "base_price": 22,
        "qty": 1,
        "row_total": 22,
        "base_row_total": 22,
        "row_total_with_discount": 0,
        "tax_amount": 0,
        "base_tax_amount": 0,
        "tax_percent": 0,
        "discount_amount": 0,
        "base_discount_amount": 0,
        "discount_percent": 0,
        "price_incl_tax": 22,
        "base_price_incl_tax": 22,
        "row_total_incl_tax": 22,
        "base_row_total_incl_tax": 22,
        "options": "[]",
        "weee_tax_applied_amount": null,
        "weee_tax_applied": null,
        "name": "Radiant Tee-M-Orange"
      },
      {
        "item_id": 7,
        "price": 18,
        "base_price": 18,
        "qty": 1,
        "row_total": 18,
        "base_row_total": 18,
        "row_total_with_discount": 0,
        "tax_amount": 0,
        "base_tax_amount": 0,
        "tax_percent": 0,
        "discount_amount": 0,
        "base_discount_amount": 0,
        "discount_percent": 0,
        "price_incl_tax": 18,
        "base_price_incl_tax": 18,
        "row_total_incl_tax": 18,
        "base_row_total_incl_tax": 18,
        "options": "[{\\"value\\":\\"Advanced Pilates & Yoga (Strength)\\",\\"label\\":\\"Downloads\\"}]",
        "weee_tax_applied_amount": null,
        "weee_tax_applied": null,
        "name": "Advanced Pilates & Yoga (Strength)"
      },
      {
        "item_id": 8,
        "price": 68,
        "base_price": 68,
        "qty": 1,
        "row_total": 68,
        "base_row_total": 68,
        "row_total_with_discount": 0,
        "tax_amount": 0,
        "base_tax_amount": 0,
        "discount_amount": 0,
        "base_discount_amount": 0,
        "discount_percent": 0,
        "price_incl_tax": 68,
        "base_price_incl_tax": 68,
        "row_total_incl_tax": 68,
        "base_row_total_incl_tax": 68,
        "options": "[{\\"value\\":\\"1 x Sprite Stasis Ball 65 cm <span class=\\\\\\"price\\\\\\">$27.00<\\\\/span>\\",\\"label\\":\\"Sprite Stasis Ball\\"},{\\"value\\":\\"1 x Sprite Foam Yoga Brick <span class=\\\\\\"price\\\\\\">$5.00<\\\\/span>\\",\\"label\\":\\"Sprite Foam Yoga Brick\\"},{\\"value\\":\\"1 x Sprite Yoga Strap 8 foot <span class=\\\\\\"price\\\\\\">$17.00<\\\\/span>\\",\\"label\\":\\"Sprite Yoga Strap\\"},{\\"value\\":\\"1 x Sprite Foam Roller <span class=\\\\\\"price\\\\\\">$19.00<\\\\/span>\\",\\"label\\":\\"Sprite Foam Roller\\"}]",
        "weee_tax_applied_amount": null,
        "weee_tax_applied": null,
        "name": "Sprite Yoga Companion Kit"
      },
      {
        "item_id": 13,
        "price": 52,
        "base_price": 52,
        "qty": 1,
        "row_total": 52,
        "base_row_total": 52,
        "row_total_with_discount": 0,
        "tax_amount": 0,
        "base_tax_amount": 0,
        "tax_percent": 0,
        "discount_amount": 0,
        "base_discount_amount": 0,
        "discount_percent": 0,
        "price_incl_tax": 52,
        "base_price_incl_tax": 52,
        "row_total_incl_tax": 52,
        "base_row_total_incl_tax": 52,
        "options": "[{\\"value\\":\\"Gray\\",\\"label\\":\\"Color\\"},{\\"value\\":\\"S\\",\\"label\\":\\"Size\\"}]",
        "weee_tax_applied_amount": null,
        "weee_tax_applied": null,
        "name": "Chaz Kangeroo Hoodie"
      }
    ],
    "total_segments": [
      {
        "code": "subtotal",
        "title": "Subtotal",
        "value": 160
      },
      {
        "code": "shipping",
        "title": "Shipping & Handling (Best Way - Table Rate)",
        "value": 5
      },
      {
        "code": "tax",
        "title": "Tax",
        "value": 0,
        "extension_attributes": {
          "tax_grandtotal_details": []
        }
      },
      {
        "code": "grand_total",
        "title": "Grand Total",
        "value": 165,
        "area": "footer"
      }
    ]
  }
}
`}</code></pre>
    </details>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`If you tried this call on your own, and the value of the `}<inlineCode parentName="p">{`shipping_amount`}</inlineCode>{` parameter is `}<inlineCode parentName="p">{`0`}</inlineCode>{`, then you did not deactivate the "Spend $50 or more - shipping is free!" cart price rule. See `}<a parentName="p" {...{
        "href": "/commerce/webapi/rest/tutorials/orders/order-config-store#deactivate-a-cart-price-rule"
      }}>{`Deactivate a cart price rule`}</a>{` for details.`}</p>
    <h3 {...{
      "id": "verify-this-step"
    }}>{`Verify this step`}</h3>
    <p>{`Sign in as the customer and go to the checkout page.`}</p>
    <p>{`The payment method is Bank Transfer, the billing and shipping addresses are displayed, and the shipping charges have been calculated.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      